/* ScrollZone — scroll-trap-free container for dashboard cards.

   The problem: a plain overflow-y:auto container hijacks the mouse wheel
   whenever the cursor passes over it, making it hard to scroll the page.

   Three states:
   - capped (default): height-capped with overflow HIDDEN — the wheel always
     scrolls the page. A fade + footer signal there's more.
   - scroll: after an explicit click on the footer, the container scrolls
     internally (overscroll-contained). Moving the mouse out hands the wheel
     back to the page automatically; re-engaging is one click.
   - expanded: grows to full content height inline — everything reads with
     natural page scrolling. Toggle back to collapse.

   The footer only renders when content actually overflows. */
import React, { useLayoutEffect, useRef, useState } from 'react';
import './ScrollZone.css';

export default function ScrollZone({ maxHeight = 420, className = '', style, children }) {
  const [mode, setMode] = useState('capped'); // capped | scroll | expanded
  const [overflowing, setOverflowing] = useState(false);
  const bodyRef = useRef(null);

  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return undefined;
    const check = () => setOverflowing(el.scrollHeight > el.clientHeight + 4);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [children, mode, maxHeight]);

  const capped = mode !== 'expanded';
  const scrolling = mode === 'scroll';

  return (
    <div className="sz-wrap">
      <div
        ref={bodyRef}
        className={`sz-body ${className}`}
        style={{
          ...style,
          maxHeight: capped ? maxHeight : 'none',
          overflowY: scrolling ? 'auto' : 'hidden',
        }}
        onMouseLeave={scrolling ? () => setMode('capped') : undefined}
      >
        {children}
      </div>
      {capped && overflowing && !scrolling && <div className="sz-fade" aria-hidden />}
      {(overflowing || mode === 'expanded') && (
        <div className="sz-bar">
          {capped && !scrolling && (
            <button type="button" className="sz-btn" onClick={() => setMode('scroll')} title="Scroll inside this card — moving the mouse away hands scrolling back to the page">
              ⇅ Scroll here
            </button>
          )}
          {scrolling && <span className="sz-note">scrolling inside — move the mouse away to release</span>}
          <button
            type="button"
            className="sz-btn"
            onClick={() => setMode(mode === 'expanded' ? 'capped' : 'expanded')}
            title={mode === 'expanded' ? 'Collapse back to a compact card' : 'Show everything inline — the page scrolls naturally'}
          >
            {mode === 'expanded' ? '▴ Collapse' : '▾ Expand all'}
          </button>
        </div>
      )}
    </div>
  );
}
